import { useGT } from 'gt-react';
import { MinusIcon, XIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { FEEDBACK_PANEL_ATTRIBUTE } from './captureViewportScreenshot';

/**
 * The docked mini-window: bottom-right, non-modal, same shell as the email
 * composer. Minimizing keeps the draft and leaves only the header bar, so the
 * user can move around the app (and screenshot it) without losing anything.
 */
export function FeedbackPanelFrame({
  title,
  minimized,
  onToggleMinimized,
  onClose,
  closeDisabled,
  children,
}: {
  title: React.ReactNode;
  minimized: boolean;
  onToggleMinimized: () => void;
  onClose: () => void;
  closeDisabled?: boolean;
  children: React.ReactNode;
}) {
  const gt = useGT();
  const dataAttribute = { [`data-${kebab(FEEDBACK_PANEL_ATTRIBUTE)}`]: '' };
  return (
    <div
      {...dataAttribute}
      className="fixed bottom-0 w-[460px] max-w-[calc(100vw-2rem-var(--agent-panel-width,0px))]"
      style={{
        // Shift left by the docked Nami panel's width so the two never overlap.
        right: 'calc(1rem + var(--agent-panel-width, 0px))',
        // Above sheets (1300) and dialogs (1400) so it stays usable over a
        // slide-in; below the 1500 popper layer so its own selects open on top.
        zIndex: 1450,
      }}
    >
      <div
        className={cn(
          'flex animate-in flex-col overflow-hidden rounded-t-lg border bg-background shadow-2xl duration-200 fade-in slide-in-from-bottom-4',
          minimized ? '' : 'max-h-[85vh]',
        )}
      >
        <div className="flex shrink-0 items-center justify-between bg-primary px-4 py-2 text-primary-foreground">
          <button
            type="button"
            onClick={onToggleMinimized}
            className="min-w-0 flex-1 truncate text-left text-sm font-medium"
          >
            {title}
          </button>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={onToggleMinimized}
              aria-label={
                minimized
                  ? gt('Expand', { context: 'feedback panel expand button' })
                  : gt('Minimize', {
                      context: 'feedback panel minimize button',
                    })
              }
              className="inline-flex size-6 items-center justify-center rounded hover:bg-white/20"
            >
              <MinusIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={closeDisabled}
              aria-label={gt('Close', {
                context: 'feedback panel close button',
              })}
              className="inline-flex size-6 items-center justify-center rounded hover:bg-white/20 disabled:opacity-50"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        {!minimized && children}
      </div>
    </div>
  );
}

/** `feedbackPanel` → `feedback-panel`, so the dataset key round-trips. */
function kebab(camel: string): string {
  return camel.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}
